// frontend/src/pages/StaffAssignmentDatabase.jsx
// 過去配置データベース (staff_assignments 直接編集画面、admin のみ、自組織のみ)
//
// できること:
//   - 期間 + 社員番号 + 術式名 検索
//   - 行ごとに編集 (インライン)、保存、削除
//   - 和名カラム表示
//
// 編集可能なカラム (全て staff_assignments 列):
//   assignment_date / role / department_name / procedure_name /
//   pairing_mode / paired_with_employee_id / paired_with_name /
//   level_at_assignment / level_after_assignment / note
//
// 自組織 RLS は Supabase 側に委ねる (staff_assignments.organization_id = user org)。

import { useEffect, useMemo, useState, useCallback } from "react";
import { ArrowLeft, Save, X, Trash2, Edit3, Plus, Database, Search, CheckSquare, Square } from "lucide-react";
import { Link, Navigate } from "react-router-dom";
import { supabase, getStaffList } from "../supabase";
import { useOrganization } from "../contexts/OrganizationContext";
import { writeClientAudit } from "../utils/auditClient";
import Header from "../components/ui/Header";

const PAGE_SIZE = 500;

const ROLE_OPTIONS = [
  { value: "scrub", label: "器械出し" },
  { value: "circulating", label: "外回り" },
];
const PAIRING_OPTIONS = [
  { value: "", label: "(未設定)" },
  { value: "instructor", label: "指導者として" },
  { value: "trainee", label: "指導付き" },
  { value: "solo", label: "独立 (1人)" },
];
const LEVEL_OPTIONS = [
  "", "未経験", "見学済み", "経験1回", "経験2回",
  "経験3回以上", "外から見守り", "独り立ち", "指導可",
];

const COLS = [
  { key: "assignment_date", label: "日付", width: 110, type: "date" },
  { key: "employee_id", label: "社員番号", width: 100, type: "text" },
  { key: "staff_name", label: "スタッフ名", width: 130, type: "computed" }, // staff_id から辿って表示 (読取専用)
  { key: "role", label: "配置役割", width: 100, type: "select", options: ROLE_OPTIONS },
  { key: "department_name", label: "診療科", width: 140, type: "text" },
  { key: "procedure_name", label: "術式", width: 200, type: "text" },
  { key: "pairing_mode", label: "配置区分", width: 130, type: "select", options: PAIRING_OPTIONS, allowEmpty: true },
  // ペア相手 1 列を 2 列に分離 (pairing_mode に応じて意味が変わるため):
  //   - pairing_mode='trainee'    → paired_with_name は「指導者」
  //   - pairing_mode='instructor' → paired_with_name は「育成した人」
  // 編集時は共通で "ペア相手" を 1 入力で受け付け、DB の paired_with_* に書き戻す。
  { key: "paired_with_employee_id", label: "ペア相手 社員番号", width: 120, type: "text" },
  { key: "mentor_name", label: "指導者", width: 130, type: "computed" },
  { key: "trainee_name", label: "育成した人", width: 130, type: "computed" },
  { key: "level_at_assignment", label: "配置時レベル", width: 110, type: "level" },
  { key: "level_after_assignment", label: "配置後レベル", width: 110, type: "level" },
  { key: "note", label: "メモ", width: 180, type: "text" },
];

// 編集モードで使う "ペア相手名" 入力は COLS に出さず別途特別表示する。
// DB 列は 1 つ (paired_with_name) なので、編集時はこれを 1 入力で受け付ける。

export default function StaffAssignmentDatabase() {
  const { currentOrganization } = useOrganization();

  // Header.js と同じ localStorage ベースで role を判定 (useOrganization.currentUser.role は
  // user_metadata 経由で我々の staff.role と一致しないケースがあるため)
  const [userRole, setUserRole] = useState("checking");
  useEffect(() => {
    const internalOrgRaw = localStorage.getItem("internal_selected_org");
    if (internalOrgRaw) {
      try { JSON.parse(internalOrgRaw); setUserRole("admin"); return; } catch (_) {}
    }
    const supabaseUser = localStorage.getItem("supabase_user");
    if (supabaseUser) {
      try {
        const d = JSON.parse(supabaseUser);
        setUserRole(d.role || "staff");
      } catch (_) {
        setUserRole("staff");
      }
    } else {
      setUserRole(null);
    }
  }, []);

  const [records, setRecords] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // staff_id → "姓 名" の辞書
  const staffNameMap = useMemo(() => {
    const m = {};
    staffList.forEach((s) => {
      const name = `${s.last_name || ""} ${s.first_name || ""}`.trim();
      m[s.id] = name || "(名無し)";
    });
    return m;
  }, [staffList]);

  // スタッフ一覧を 1 回だけ取得
  useEffect(() => {
    (async () => {
      if (!currentOrganization?.id) return;
      try {
        const list = await getStaffList(currentOrganization.id);
        setStaffList(list || []);
      } catch (e) {
        console.error("staff fetch failed:", e);
      }
    })();
  }, [currentOrganization?.id]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Filters
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [empIdFilter, setEmpIdFilter] = useState("");
  const [procNameFilter, setProcNameFilter] = useState("");
  const [pairingFilter, setPairingFilter] = useState("all"); // all / instructor / trainee / solo / none

  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const fetchPage = useCallback(async (reset = false) => {
    if (!currentOrganization?.id) return;
    setLoading(true);
    setError("");
    try {
      const curPage = reset ? 0 : page;
      let q = supabase
        .from("staff_assignments")
        .select("id, staff_id, employee_id, procedure_id, procedure_name, department_name, role, assignment_date, pairing_mode, paired_with_staff_id, paired_with_employee_id, paired_with_name, level_at_assignment, level_after_assignment, note")
        .eq("organization_id", currentOrganization.id)
        .order("assignment_date", { ascending: false })
        .range(curPage * PAGE_SIZE, (curPage + 1) * PAGE_SIZE - 1);
      if (dateFrom) q = q.gte("assignment_date", dateFrom);
      if (dateTo) q = q.lte("assignment_date", dateTo);
      if (empIdFilter.trim()) q = q.ilike("employee_id", `%${empIdFilter.trim()}%`);
      if (procNameFilter.trim()) q = q.ilike("procedure_name", `%${procNameFilter.trim()}%`);
      if (pairingFilter === "none") q = q.is("pairing_mode", null);
      else if (pairingFilter !== "all") q = q.eq("pairing_mode", pairingFilter);

      const { data, error: e } = await q;
      if (e) throw e;
      const list = data || [];
      setHasMore(list.length === PAGE_SIZE);
      if (reset) {
        setRecords(list);
        setPage(0);
      } else {
        setRecords((prev) => [...prev, ...list]);
      }
    } catch (e) {
      console.error(e);
      setError(e.message || "読み込み失敗");
    } finally {
      setLoading(false);
    }
  }, [currentOrganization?.id, dateFrom, dateTo, empIdFilter, procNameFilter, pairingFilter, page]);

  useEffect(() => {
    fetchPage(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganization?.id]);

  const handleSearch = () => {
    setEditingId(null);
    fetchPage(true);
  };

  const handleLoadMore = async () => {
    const nextPage = page + 1;
    setPage(nextPage);
    const curPage = nextPage;
    setLoading(true);
    try {
      let q = supabase
        .from("staff_assignments")
        .select("id, staff_id, employee_id, procedure_id, procedure_name, department_name, role, assignment_date, pairing_mode, paired_with_staff_id, paired_with_employee_id, paired_with_name, level_at_assignment, level_after_assignment, note")
        .eq("organization_id", currentOrganization.id)
        .order("assignment_date", { ascending: false })
        .range(curPage * PAGE_SIZE, (curPage + 1) * PAGE_SIZE - 1);
      if (dateFrom) q = q.gte("assignment_date", dateFrom);
      if (dateTo) q = q.lte("assignment_date", dateTo);
      if (empIdFilter.trim()) q = q.ilike("employee_id", `%${empIdFilter.trim()}%`);
      if (procNameFilter.trim()) q = q.ilike("procedure_name", `%${procNameFilter.trim()}%`);
      if (pairingFilter === "none") q = q.is("pairing_mode", null);
      else if (pairingFilter !== "all") q = q.eq("pairing_mode", pairingFilter);
      const { data, error: e } = await q;
      if (e) throw e;
      const list = data || [];
      setHasMore(list.length === PAGE_SIZE);
      setRecords((prev) => [...prev, ...list]);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (r) => {
    setEditingId(r.id);
    setEditForm({ ...r });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setSaving(true);
    try {
      const update = {};
      COLS.forEach((c) => {
        if (c.type === "computed") return; // 計算カラム (staff_name など) は DB に書かない
        let v = editForm[c.key];
        if (v === "") v = null;
        update[c.key] = v;
      });
      const { error: e } = await supabase
        .from("staff_assignments")
        .update(update)
        .eq("id", editingId)
        .eq("organization_id", currentOrganization.id);
      if (e) throw e;
      await writeClientAudit({
        organizationId: currentOrganization.id,
        actionType: "update",
        targetTable: "staff_assignments",
        targetId: editingId,
        targetSummary: `過去配置 1 行を編集 (${update.employee_id || ""} / ${update.assignment_date || ""})`,
        changes: update,
        success: true,
      });
      setRecords((prev) => prev.map((r) => (r.id === editingId ? { ...r, ...update } : r)));
      setEditingId(null);
      setEditForm({});
    } catch (e) {
      await writeClientAudit({
        organizationId: currentOrganization?.id,
        actionType: "update",
        targetTable: "staff_assignments",
        targetId: editingId,
        targetSummary: `過去配置 1 行の編集に失敗`,
        success: false,
        errorMessage: e.message,
      });
      alert(`保存失敗: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const deleteRow = async (r) => {
    if (!window.confirm(`この行を削除しますか？\n${r.assignment_date} / ${r.employee_id} / ${r.procedure_name || "(未設定)"}`)) return;
    try {
      const { error: e } = await supabase
        .from("staff_assignments")
        .delete()
        .eq("id", r.id)
        .eq("organization_id", currentOrganization.id);
      if (e) throw e;
      await writeClientAudit({
        organizationId: currentOrganization.id,
        actionType: "delete",
        targetTable: "staff_assignments",
        targetId: r.id,
        targetSummary: `過去配置 1 行を削除 (${r.employee_id} / ${r.assignment_date} / ${r.procedure_name || ""})`,
        success: true,
      });
      setRecords((prev) => prev.filter((x) => x.id !== r.id));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(r.id);
        return next;
      });
    } catch (e) {
      await writeClientAudit({
        organizationId: currentOrganization?.id,
        actionType: "delete",
        targetTable: "staff_assignments",
        targetId: r.id,
        targetSummary: `過去配置 1 行の削除に失敗`,
        success: false,
        errorMessage: e.message,
      });
      alert(`削除失敗: ${e.message}`);
    }
  };

  // ===== 一括選択 =====
  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const allSelected = records.length > 0 && records.every((r) => selectedIds.has(r.id));
  const someSelected = selectedIds.size > 0 && !allSelected;
  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(records.map((r) => r.id)));
    }
  };

  // ===== 一括削除 =====
  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const first = window.confirm(
      `選択中の ${selectedIds.size} 件を本当に削除しますか？\n\nこの操作は元に戻せません。`
    );
    if (!first) return;
    // ダブル確認
    const second = window.confirm(
      `最終確認: ${selectedIds.size} 件のレコードを削除します。 よろしいですか？`
    );
    if (!second) return;

    setBulkDeleting(true);
    try {
      const ids = [...selectedIds];
      // Supabase の .in フィルタで一括削除 (大量の場合はチャンク分割)
      const CHUNK = 500;
      for (let i = 0; i < ids.length; i += CHUNK) {
        const slice = ids.slice(i, i + CHUNK);
        const { error: e } = await supabase
          .from("staff_assignments")
          .delete()
          .in("id", slice)
          .eq("organization_id", currentOrganization.id);
        if (e) throw e;
      }
      await writeClientAudit({
        organizationId: currentOrganization.id,
        actionType: "delete",
        targetTable: "staff_assignments",
        targetSummary: `過去配置 ${ids.length} 件を一括削除`,
        changes: { count: ids.length, sampleIds: ids.slice(0, 5) },
        success: true,
      });
      setRecords((prev) => prev.filter((x) => !selectedIds.has(x.id)));
      setSelectedIds(new Set());
      alert(`${ids.length} 件を削除しました`);
    } catch (e) {
      await writeClientAudit({
        organizationId: currentOrganization?.id,
        actionType: "delete",
        targetTable: "staff_assignments",
        targetSummary: `過去配置 ${selectedIds.size} 件の一括削除に失敗`,
        success: false,
        errorMessage: e.message,
      });
      alert(`一括削除失敗: ${e.message}`);
    } finally {
      setBulkDeleting(false);
    }
  };

  // ===== Admin guard =====
  if (userRole === "checking") return null; // 判定中は何も描画しない (リダイレクトしない)
  if (userRole !== "admin") return <Navigate to="/home" replace />;

  return (
    <div style={styles.page}>
      <Header />
      <div style={styles.content}>
        <div style={styles.pageHeader}>
          <Link to="/education-dashboard" style={styles.backLink}>
            <ArrowLeft size={14} /> 教育状況分析
          </Link>
          <h1 style={styles.title}>
            <Database size={20} style={{ verticalAlign: "middle", marginRight: 8, color: "#1d4ed8" }} />
            過去配置データベース
          </h1>
          <p style={styles.subtitle}>
            <code>staff_assignments</code> テーブルを直接編集します (管理者のみ、自組織のみ)。
            正しくないデータの修正や削除に使用してください。
          </p>
        </div>

        {error && <div style={styles.errorBox}>{error}</div>}

        <div style={styles.filterBar}>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>日付 From</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={styles.filterInput} />
          </div>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>日付 To</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={styles.filterInput} />
          </div>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>社員番号</label>
            <input type="text" value={empIdFilter} onChange={(e) => setEmpIdFilter(e.target.value)} placeholder="部分一致" style={styles.filterInput} />
          </div>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>術式名</label>
            <input type="text" value={procNameFilter} onChange={(e) => setProcNameFilter(e.target.value)} placeholder="部分一致" style={styles.filterInput} />
          </div>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>指導役割</label>
            <select value={pairingFilter} onChange={(e) => setPairingFilter(e.target.value)} style={styles.filterInput}>
              <option value="all">全て</option>
              <option value="instructor">指導者</option>
              <option value="trainee">被指導者</option>
              <option value="solo">独立</option>
              <option value="none">(未設定)</option>
            </select>
          </div>
          <button onClick={handleSearch} style={styles.searchBtn}>
            <Search size={14} /> 検索
          </button>
        </div>

        <div style={styles.metaBar}>
          <span style={{ fontSize: 12, color: "#64748b" }}>
            表示中 {records.length} 件 {hasMore && "(続きあり)"}
            {selectedIds.size > 0 && (
              <strong style={{ marginLeft: 10, color: "#b91c1c" }}>
                / {selectedIds.size} 件選択中
              </strong>
            )}
          </span>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {loading && <span style={{ fontSize: 12, color: "#1d4ed8" }}>読み込み中...</span>}
            {selectedIds.size > 0 && (
              <>
                <button onClick={() => setSelectedIds(new Set())} style={styles.clearSelBtn}>
                  選択解除
                </button>
                <button
                  onClick={bulkDelete}
                  disabled={bulkDeleting}
                  style={{
                    ...styles.bulkDeleteBtn,
                    opacity: bulkDeleting ? 0.5 : 1,
                    cursor: bulkDeleting ? "not-allowed" : "pointer",
                  }}
                >
                  <Trash2 size={14} />
                  {bulkDeleting ? "削除中..." : `選択した ${selectedIds.size} 件を削除`}
                </button>
              </>
            )}
          </div>
        </div>

        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, width: 36, textAlign: "center" }}>
                  <button
                    type="button"
                    onClick={toggleAll}
                    style={styles.chkBtn}
                    title={allSelected ? "全解除" : "全選択"}
                  >
                    {allSelected ? (
                      <CheckSquare size={16} style={{ color: "#1d4ed8" }} />
                    ) : someSelected ? (
                      <CheckSquare size={16} style={{ color: "#94a3b8" }} />
                    ) : (
                      <Square size={16} style={{ color: "#94a3b8" }} />
                    )}
                  </button>
                </th>
                {COLS.map((c) => (
                  <th key={c.key} style={{ ...styles.th, minWidth: c.width, width: c.width }}>
                    {c.label}
                  </th>
                ))}
                <th style={{ ...styles.th, width: 110, textAlign: "center", position: "sticky", right: 0, zIndex: 2, background: "#f8fafc" }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {records.length === 0 && !loading && (
                <tr>
                  <td colSpan={COLS.length + 2} style={{ padding: 30, textAlign: "center", color: "#94a3b8" }}>
                    該当するレコードがありません
                  </td>
                </tr>
              )}
              {records.map((r) => {
                const isEditing = editingId === r.id;
                const isSelected = selectedIds.has(r.id);
                const pm = r.pairing_mode;
                const pairedName = r.paired_with_name || "";
                const computedValues = {
                  staff_name: staffNameMap[r.staff_id] || "—",
                  // pairing_mode に応じて paired_with_name をどちらの列に出すか切り替え
                  mentor_name: pm === "trainee" ? pairedName : "",
                  trainee_name: pm === "instructor" ? pairedName : "",
                };
                return (
                  <tr key={r.id} style={{ background: isEditing ? "#eff6ff" : isSelected ? "#fef3c7" : undefined }}>
                    <td style={{ ...styles.td, textAlign: "center" }}>
                      <button
                        type="button"
                        onClick={() => toggleOne(r.id)}
                        style={styles.chkBtn}
                      >
                        {isSelected
                          ? <CheckSquare size={16} style={{ color: "#1d4ed8" }} />
                          : <Square size={16} style={{ color: "#94a3b8" }} />}
                      </button>
                    </td>
                    {COLS.map((c) => {
                      const displayVal = c.type === "computed" ? computedValues[c.key] : r[c.key];
                      // 編集モード時の mentor_name / trainee_name の扱い:
                      //   DB 列は 1 本 (paired_with_name) なので、現在の pairing_mode に合致する列に
                      //   入力ボックスを 1 つだけ出す。 もう一方は "—" でクリアに。
                      let content;
                      if (isEditing && c.type === "computed") {
                        const pm = editForm.pairing_mode;
                        if (
                          (c.key === "mentor_name" && pm === "trainee") ||
                          (c.key === "trainee_name" && pm === "instructor")
                        ) {
                          content = (
                            <input
                              type="text"
                              value={editForm.paired_with_name ?? ""}
                              onChange={(e) => setEditForm({ ...editForm, paired_with_name: e.target.value })}
                              style={inputStyle}
                              placeholder={c.key === "mentor_name" ? "指導者名" : "育成した人の名前"}
                            />
                          );
                        } else {
                          content = <span style={{ color: "#cbd5e1" }}>—</span>;
                        }
                      } else if (isEditing && c.type !== "computed") {
                        content = renderEditInput(c, editForm[c.key], (v) => setEditForm({ ...editForm, [c.key]: v }));
                      } else {
                        content = renderDisplay(c, displayVal);
                      }
                      return (
                        <td key={c.key} style={styles.td}>{content}</td>
                      );
                    })}
                    <td style={{
                      ...styles.td,
                      textAlign: "center",
                      position: "sticky",
                      right: 0,
                      // 行の背景色と揃える (sticky セルは下の内容を透過させないため)
                      background: isEditing ? "#eff6ff" : isSelected ? "#fef3c7" : "#fff",
                      boxShadow: "-4px 0 6px -3px rgba(0,0,0,0.08)", // 左側に軽い影で区切り感
                      zIndex: 1,
                    }}>
                      {isEditing ? (
                        <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                          <button onClick={saveEdit} disabled={saving} style={{ ...styles.iconBtn, background: "#10b981", color: "#fff" }} title="保存">
                            <Save size={14} />
                          </button>
                          <button onClick={cancelEdit} style={{ ...styles.iconBtn, background: "#f3f4f6", color: "#334155" }} title="キャンセル">
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                          <button onClick={() => startEdit(r)} style={{ ...styles.iconBtn, background: "#eff6ff", color: "#1d4ed8" }} title="編集">
                            <Edit3 size={14} />
                          </button>
                          <button onClick={() => deleteRow(r)} style={{ ...styles.iconBtn, background: "#fef2f2", color: "#b91c1c" }} title="削除">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {hasMore && (
          <div style={{ textAlign: "center", marginTop: 16 }}>
            <button onClick={handleLoadMore} disabled={loading} style={styles.loadMoreBtn}>
              <Plus size={14} /> さらに {PAGE_SIZE} 件読み込む
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Render helpers
// =============================================================================
function renderDisplay(c, v) {
  if (v == null || v === "") return <span style={{ color: "#94a3b8" }}>—</span>;
  if (c.type === "select" || c.type === "level") {
    if (c.type === "select") {
      const match = c.options.find((o) => o.value === v);
      return match ? match.label : v;
    }
    return v;
  }
  return v;
}

function renderEditInput(c, value, onChange) {
  const v = value ?? "";
  if (c.type === "date") {
    return <input type="date" value={v} onChange={(e) => onChange(e.target.value)} style={inputStyle} />;
  }
  if (c.type === "select") {
    return (
      <select value={v} onChange={(e) => onChange(e.target.value)} style={inputStyle}>
        {c.allowEmpty && !c.options.find((o) => o.value === "") && <option value="">(未設定)</option>}
        {c.options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    );
  }
  if (c.type === "level") {
    return (
      <select value={v} onChange={(e) => onChange(e.target.value)} style={inputStyle}>
        {LEVEL_OPTIONS.map((l) => (
          <option key={l} value={l}>{l || "(未設定)"}</option>
        ))}
      </select>
    );
  }
  return <input type="text" value={v} onChange={(e) => onChange(e.target.value)} style={inputStyle} />;
}

const inputStyle = {
  width: "100%", padding: "4px 6px", fontSize: 12,
  border: "1.5px solid #cbd5e1", borderRadius: 4, boxSizing: "border-box",
};

const styles = {
  page: { minHeight: "100vh", background: "#f8fafc" },
  content: { maxWidth: 1600, margin: "0 auto", padding: 24 },
  pageHeader: { marginBottom: 16 },
  backLink: {
    display: "inline-flex", alignItems: "center", gap: 4,
    fontSize: 12, color: "#1d4ed8", textDecoration: "none", marginBottom: 6,
  },
  title: { fontSize: 22, fontWeight: 700, color: "#0f172a", margin: "4px 0" },
  subtitle: { fontSize: 12, color: "#64748b", margin: 0 },
  errorBox: {
    background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c",
    padding: "8px 12px", borderRadius: 8, fontSize: 12, marginBottom: 10,
  },
  filterBar: {
    display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end",
    background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10,
    padding: 12, marginBottom: 12,
  },
  filterGroup: { display: "flex", flexDirection: "column", gap: 4 },
  filterLabel: { fontSize: 11, color: "#64748b", fontWeight: 600 },
  filterInput: {
    padding: "6px 8px", border: "1.5px solid #cbd5e1", borderRadius: 6,
    fontSize: 13, minWidth: 120, background: "#fff",
  },
  searchBtn: {
    display: "inline-flex", alignItems: "center", gap: 4,
    padding: "7px 16px", background: "#1d4ed8", color: "#fff",
    border: "none", borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: "pointer",
  },
  metaBar: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 },
  tableWrapper: {
    background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10,
    overflow: "auto", maxHeight: 650,
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 12 },
  th: {
    padding: "8px 10px", textAlign: "left",
    borderBottom: "2px solid #e2e8f0",
    fontSize: 11, color: "#475569", fontWeight: 700,
    background: "#f8fafc", position: "sticky", top: 0, zIndex: 1,
  },
  td: {
    padding: "6px 10px", borderBottom: "1px solid #f1f5f9", color: "#0f172a",
    verticalAlign: "middle",
  },
  iconBtn: {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    width: 28, height: 28, border: "none", borderRadius: 6, cursor: "pointer",
  },
  loadMoreBtn: {
    display: "inline-flex", alignItems: "center", gap: 4,
    padding: "8px 20px", background: "#fff", color: "#1d4ed8",
    border: "1.5px solid #bfdbfe", borderRadius: 8, cursor: "pointer",
    fontSize: 13, fontWeight: 600,
  },
  chkBtn: {
    background: "none", border: "none", padding: 0, cursor: "pointer",
    display: "inline-flex", alignItems: "center", justifyContent: "center",
  },
  bulkDeleteBtn: {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "7px 14px", background: "#dc2626", color: "#fff",
    border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700,
  },
  clearSelBtn: {
    padding: "6px 12px", background: "#f1f5f9", color: "#334155",
    border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 12, cursor: "pointer",
  },
};
